import { changePackageHeading } from '@/features/dashboard/bookingChanges/types/BookingChangeKind.utils.ts';
import { cn } from '@/lib/utils';
import NoItems from '@/components/ui/NoItems.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { ChangePackageHistoryVm } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import {
  getChangeImpactLabel,
  getChangePackageStatusLabel,
  getChangePackageStatusTone,
} from '@/features/dashboard/bookingChanges/types/ChangeViewModel.utils.ts';
import { formatDateTime } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { Card } from '@/components/ui/Card.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { changeHistoryGridClass } from './changeHistoryGrid';
import RejectionCommentCell from './RejectionCommentCell';
import { pluralizeItems } from '@/features/dashboard/bookingChanges/utils/plurals.ts';

const columnHeaderCellClass = cn('pb-2.5', columnLabelClass);

interface ChangeHistoryTableProps extends React.HTMLAttributes<HTMLDivElement> {
  packages: ChangePackageHistoryVm[];
  /** `id paczki` → uzasadnienie rezygnacji; edycje go nie mają. */
  justifications?: Record<string, string>;
}

/**
 * Zakładka „Historia" ekranu rezerwacji — paczki, które wyszły ze stanu szkicu:
 * wprowadzone, odrzucone przez klienta i zdjęte przez operatora. Otwarty szkic
 * świadomie tu nie trafia: żyje w trybie zmiany i ogłasza się belką pod
 * zakładkami, dopóki operator go nie wprowadzi albo nie odrzuci.
 *
 * Czego wiersz nie mówi: **czego paczka dotyczyła**. Lista paczek nie niesie
 * zakresu i nieść nie będzie — nazwy wariantów siedzą w katalogu, a nie
 * w pozycji paczki, więc zakres wymaga wejścia na szczegóły. Zamiast zmyślonej
 * etykiety wiersz podaje stan paczki, który jest w kontrakcie.
 *
 * Siatka udaje tabelę, więc role ARIA nadajemy ręcznie — bez nich czytnik
 * ekranu nie zobaczy ani nagłówków kolumn, ani podziału na wiersze.
 */
const ChangeHistoryTable = ({
  packages,
  justifications = {},
  className,
  ...props
}: ChangeHistoryTableProps) => (
  <Card className={cn('overflow-hidden', className)} {...props}>
    <div className="flex items-baseline gap-2.5 px-7 pb-3.5 pt-5">
      <span className={columnLabelClass}>Historia paczek</span>
      <span className="text-xs font-semibold text-neutral-400">
        zamknięte paczki tej rezerwacji
      </span>
    </div>

    {packages.length === 0 ? (
      <div className="px-7 pb-6">
        <NoItems
          title="Brak zamkniętych paczek"
          description="Ta rezerwacja nie była jeszcze zmieniana. Pierwsza wprowadzona paczka pojawi się tutaj."
        />
      </div>
    ) : (
      <div role="table" aria-label="Historia paczek">
        <div
          role="row"
          className={cn(changeHistoryGridClass, 'border-b border-neutral-100')}
        >
          <div role="columnheader" className={columnHeaderCellClass}>
            Paczka
          </div>
          <div role="columnheader" className={columnHeaderCellClass}>
            Stan
          </div>
          <div role="columnheader" className={columnHeaderCellClass}>
            Data
          </div>
          <div
            role="columnheader"
            className={cn(columnHeaderCellClass, 'text-right')}
          >
            Wpływ
          </div>
        </div>

        {packages.map((changePackage) => (
          <div
            key={changePackage.id}
            role="row"
            className={cn(
              changeHistoryGridClass,
              'items-baseline border-b border-dashed border-neutral-100',
            )}
          >
            <div role="cell" className="py-3.5">
              <div className="text-[15px] font-semibold text-ink">
                {changePackageHeading(
                  changePackage.kind,
                  changePackage.packageNumber,
                )}
              </div>
              <div className="text-[13px] text-neutral-500">
                {pluralizeItems(changePackage.itemCount)}
              </div>
              {/* Uzasadnienie jest obowiązkowe przy rezygnacji (art. 47), więc
                  historia ma je pokazać, a nie chować w szczegółach pozycji. */}
              {justifications[changePackage.id] && (
                <div className="wrap-anywhere mt-1 text-[13px] text-neutral-500">
                  {justifications[changePackage.id]}
                </div>
              )}
            </div>
            <div role="cell" className="py-3.5 pr-5">
              <StatusDot
                className="text-[15px]"
                tone={getChangePackageStatusTone(changePackage.status)}
                label={getChangePackageStatusLabel(changePackage.status)}
              />
            </div>
            <div role="cell" className="py-3.5">
              <div className="text-[15px] text-neutral-800">
                {formatDateTime(changePackage.occurredAt)}
              </div>
              <div className="text-[13px] text-neutral-500">
                {changePackage.author ?? 'autor nieznany'}
              </div>
            </div>
            <div
              role="cell"
              className={cn(
                'py-3.5 text-right text-[15px] font-semibold tabular-nums',
                changePackage.impact === null ? 'text-neutral-400' : 'text-ink',
              )}
            >
              {getChangeImpactLabel(changePackage.impact)}
            </div>
            {changePackage.rejectionComment && (
              <RejectionCommentCell
                comment={changePackage.rejectionComment}
                className="col-span-full -mt-1.5 pb-3.5"
              />
            )}
          </div>
        ))}
      </div>
    )}

    <div className="text-pretty px-7 pb-[18px] pt-3.5 text-[13px] text-neutral-500">
      Kwotę zna wyłącznie paczka wprowadzona — odrzucona i anulowana nigdy nie
      stanęły w rachunku. Otwarty szkic żyje w trybie zmiany, dopóki go nie
      wprowadzisz lub nie odrzucisz.
    </div>
  </Card>
);

export default ChangeHistoryTable;
